'use client'

import { useCallback, useEffect, useRef } from 'react'
import { MAP_DOMAIN, MapMessageType } from './constants'

export interface MapBridgeMessage {
  type: MapMessageType | string
  data: any
}

/**
 * Owns the <iframe> ref plus the postMessage plumbing to/from the map
 * micro-app at MAP_DOMAIN. `onMessage` is read from a ref internally, so
 * callers can pass an inline function without needing to memoize it.
 */
export function useMapBridge(onMessage: (message: MapBridgeMessage) => void) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  const postToFrame = useCallback((type: MapMessageType | string, data: any = {}) => {
    iframeRef.current?.contentWindow?.postMessage({ type, data }, MAP_DOMAIN)
  }, [])

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      if (!MAP_DOMAIN.includes(event.origin)) return

      const { type, data } = event.data || {}
      if (!type) return

      onMessageRef.current({ type, data })
    }

    window.addEventListener('message', handleWindowMessage)
    return () => window.removeEventListener('message', handleWindowMessage)
  }, [])

  return { iframeRef, postToFrame }
}

export function buildMapSrc({
  lat,
  lng,
  zoom,
  mode,
}: {
  lat: number
  lng: number
  zoom: number
  mode: string
}) {
  return `${MAP_DOMAIN}/map?lat=${lat}&lng=${lng}&zoom=${zoom}&mode=${mode}`
}
